import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import { CalendarHeaderComponent } from 'src/app/shared/components/calendar-header/calendar-header.component';

class CustomDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }
}

@Component({
    selector: 'app-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['../../../styles.scss', './date-range-picker.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { dateInput: 'MM/DD/YYYY' },
                display: {
                    dateInput: 'MM/DD/YYYY',
                    monthYearLabel: 'MMMM YYYY',
                    dateA11yLabel: 'MM/DD/YYYY',
                    monthYearA11yLabel: 'MM YYYY',
                },
            },
        },
    ],
    standalone: false
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
  // Shared Figma-styled calendar header (chevrons + white-chip tooltip).
  public calendarHeader = CalendarHeaderComponent;
  @Input() label = '';
  @Input() placeholderStart = 'From';
  @Input() placeholderEnd = 'To';
  @Input() parentFormGroup: FormGroup = new FormGroup({});
  @Input() startDateControlName = 'startDate';
  @Input() endDateControlName = 'endDate';
  @Input() error = '';
  @Input() min: any = moment('01/01/1900', 'L');
  @Input() max: any = moment('12/31/2200', 'L');
  @Output() checkForErrors = new EventEmitter();
  @Output() dateRangeChange = new EventEmitter<{ startDate: any; endDate: any }>();

  @ViewChild('picker') picker: MatDateRangePicker<moment.Moment>;
  fieldSubscription: Subscription | undefined;
  isRequired = false;
  isDisabled = false;

  constructor() {}

  @HostListener('focusout')
  onBlur() {
    if (!this.error) {
      const startControl = this.parentFormGroup.controls[this.startDateControlName];
      const endControl = this.parentFormGroup.controls[this.endDateControlName];
      if (startControl) {
        startControl.markAsUntouched();
      }
      if (endControl) {
        endControl.markAsUntouched();
      }
    }
  }

  ngOnInit(): void {
    const startControl = this.parentFormGroup.controls[this.startDateControlName];
    const endControl = this.parentFormGroup.controls[this.endDateControlName];

    if (startControl && endControl) {
      this.isDisabled = startControl.status === 'DISABLED';
      this.fieldSubscription = endControl.valueChanges.subscribe((value) => {
        // Set max value
        if (value?._i?.length > 10 && value?.isAfter(this.max)) {
          this.parentFormGroup.controls[this.endDateControlName].setValue(this.max);
        }

        // Set min value (end date should be after start date)
        const startValue = startControl.value;
        if (startValue && value?._i?.length > 10 && value?.isBefore(startValue)) {
          this.parentFormGroup.controls[this.endDateControlName].setValue(startValue);
        }

        // If field is marked as touched, mark it as untouched
        if (endControl?.touched) {
          endControl.markAsUntouched();
        }

        // Get errors on the form field
        const errors = endControl?.errors;
        if (errors) {
          // Clear errors on the form field
          endControl.setErrors(null);
          // Clear out the error messages on the screen (parent component)
          this.checkForErrors.emit(this.endDateControlName);
          // Add errors again to the form field for showing messages in the future
          endControl.setErrors(errors);
        } else {
          // Clear out the error messages on the screen (parent component)
          this.checkForErrors.emit(this.endDateControlName);
        }

        // Emit date range change
        if (startValue && value) {
          this.dateRangeChange.emit({
            startDate: startValue,
            endDate: value,
          });
        }
      });
    }

    // Check if field is required
    if (startControl?.validator) {
      const validator = startControl.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  openPicker(): void {
    if (!this.isDisabled && this.picker) {
      this.picker.open();
    }
  }

  onPickerOpened(): void {
    // Optional: Handle when picker opens
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }
}
